<template>
    <div class="maintenance-modal-form">
        <CoreTwoColumnsRow class="contract-data-item-row">
            <template #left>
                <div
                    class="flex-grow-1 flex-shrink-1 flex-basis-35"
                    :condensed="true"
                >
                    <span>
                        {{ $t('§contracts.as_of') }}
                    </span>
                </div>
            </template>
            <template #right>
                <CoreTwoColumnsRow :style="{ padding: 0 }">
                    <template #left>
                        <div
                            class="flex-grow-1 flex-shrink-1 flex-basis-55"
                            :condensed="true"
                        >
                            <span>{{ $t(props.valueColumnHeader) }}</span>
                        </div>
                    </template>
                    <template #right>
                        <div
                            class="flex-grow-1 flex-shrink-1 flex-basis-60"
                            :condensed="true"
                        >
                            <CoreTwoColumnsRow :style="{ padding: 0 }">
                                <template #left>
                                    <span>
                                        {{ $t('§contracts.percent_change') }}
                                    </span>
                                </template>
                                <template #right> &nbsp; </template>
                            </CoreTwoColumnsRow>
                        </div>
                    </template>
                </CoreTwoColumnsRow>
            </template>
        </CoreTwoColumnsRow>
        <CoreTwoColumnsRow
            v-for="(item, as_of) in props.history"
            class="contract-data-item-row"
        >
            <template #left>
                <div
                    class="flex-grow-1 flex-shrink-1 flex-basis-35"
                    :condensed="true"
                >
                    <span>
                        {{ displayAsDate(parseISO_(item.as_of)) }}
                    </span>
                </div>
            </template>
            <template #right>
                <CoreTwoColumnsRow :style="{ padding: 0 }">
                    <template #left>
                        <div
                            class="flex-grow-1 flex-shrink-1 flex-basis-55"
                            :condensed="true"
                        >
                            <span>
                                {{ isMaintenanceModal ? displayAsCurrency(item.value, 2, contractData.currency_code) : displayAsCurrency(item.value, billingUnitDecimals(contractData.billing_unit_id), contractData.currency_code) }}
                            </span>
                            <span
                                v-if="isMaintenanceModal"
                                class="font-color-25 font-italic"
                            >
                                <span> </span>
                                ({{
                                    displayAsCurrency(
                                        item.value / 12,
                                        2,
                                        contractData.currency_code
                                    )
                                }}
                                {{ $t('§contracts.per_month') }})
                            </span>
                        </div>
                    </template>
                    <template #right>
                        <div
                            class="flex-grow-1 flex-shrink-1 flex-basis-60"
                        >
                            <CoreTwoColumnsRow :style="{ padding: 0 }">
                                <template #left>
                                    <span>
                                        {{ displayAsPercent(item.change, 2) }}
                                    </span>
                                </template>
                                <template #right>
                                    <CoreButton
                                        v-if="hasPermission('manage contracts')"
                                        label="§ui_button.delete"
                                        icon="pi-trash"
                                        variant="danger"
                                        @click="handleDelete(item.id)"
                                        :danger="true"
                                    />
                                    <span v-else> &nbsp; </span>
                                </template>
                            </CoreTwoColumnsRow>
                        </div>
                    </template>
                </CoreTwoColumnsRow>
            </template>
        </CoreTwoColumnsRow>
        <CoreTwoColumnsRow
            v-if="hasPermission('manage contracts')"
            class="contract-data-item-row add-new-row"
        >
            <template #left>
                <div class="flex-grow-1 flex-shrink-1 flex-basis-45">
                    <CoreDateInput
                        v-if="showForm"
                        v-model="inputs.as_of"
                        class="flex-grow-1 flex-shrink-1 flex-basis-50"
                        :placeholder="$t('§ui.placeholder_tcf_cost')"
                        :condensed="true"
                    />
                    <CoreButton
                        v-else
                        label="§ui_button.add_new"
                        icon="pi-plus"
                        @click="showForm = true"
                        :colorPrimary="true"
                    />
                </div>
            </template>
            <template #right>
                <CoreTwoColumnsRow :style="{ padding: 0 }">
                    <template #left>
                        <div class="flex-grow-1 flex-shrink-1 flex-basis-65">
                            <CurrencyPercentageInput
                                v-if="showForm"
                                :baseValue="props.current_value"
                                :currencyDigits="currencyDecimals"
                                :maxPercent="200"
                                v-model="inputs.value"
                                class="flex-grow-1 flex-shrink-1 flex-basis-65"
                                :inverse="true"
                                :showBorder="false"
                                currencyInputTextAlign="left"
                                :currency="contractData.currency_code"
                                :percentageDigits="2"
                            />
                        </div>
                    </template>
                    <template #right>
                        <div class="flex-grow-1 flex-shrink-1 flex-basis-35">
                            <CoreButton
                                v-if="showForm"
                                :disabled="!isValid"
                                @click="handleSave()"
                                label="§ui_button.save"
                                :floatRight="true"
                                icon="pi-save"
                            />
                        </div>
                    </template>
                </CoreTwoColumnsRow>
            </template>
        </CoreTwoColumnsRow>
    </div>
</template>

<script>
import { parseISO, format } from 'date-fns'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreDateInput from '@/components/core/CoreDateInput.vue'
import CoreCurrencyInput from '@/components/core/CoreCurrencyInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CurrencyPercentageInput from '@/views/ClickAndFinance/components/CurrencyPercentageInput.vue'

const MAINTENANCE_TYPE = 'maintenance'

export default {
    name: 'MaintenanceModal',

    components: {
        CoreTwoColumnsRow,
        CoreDateInput,
        CoreCurrencyInput,
        CoreButton,
        CurrencyPercentageInput,
    },

    data() {
        return {
            inputs: {
                value: 0,
                as_of: null,
            },
            showForm: false,
            v$: {},
            props: {},
        }
    },

    computed: {
        isValid() {
            return !this.v$.$invalid
        },

        rules() {
            return {
                as_of: {
                    required,
                },
                value: {
                    required,
                },
            }
        },

        contractData() {
            return this.contractStore.contractData
        },

        type() {
            return this.props.tcfType
        },

        isMaintenanceModal() {
            return this.type === MAINTENANCE_TYPE
        },

        currencyDecimals() {
            return this.isMaintenanceModal ? 2 : this.billingUnitDecimals(this.contractData.billing_unit_id)
        }
    },

    created() {
        this.modalStore = useModalStore()
        this.contractStore = useContractStore()
        this.props = this.modalStore.data
        this.inputs.value = this.modalStore.data.current_value
    },

    mounted() {
        this.v$ = useVuelidate(this.rules, this.inputs)
    },

    methods: {
        async handleSave() {
            if (!this.contractStore.saving_temporary_contract_figure) {
                await this.contractStore.addTemporaryContractFigure({
                    value: this.inputs.value,
                    as_of: format(this.inputs.as_of, 'Y-M-d'),
                    type: this.type,
                })
                this.contractStore.getContractData(this.contractData.id)
                this.modalStore.setClose()
            }
        },

        async handleDelete(id) {
            if (!this.contractStore.deleting_temporary_contract_figure) {
                await this.contractStore.deleteTemporaryContractFigure({
                    tcfId: id,
                })
                this.contractStore.getContractData(this.contractData.id)
                this.props.history = this.props.history.filter((item) => item.id !== id)
            }
        },

        parseISO_(s) {
            return parseISO(s)
        },
    },
}
</script>

<style>
.maintenance-modal-form {
    padding: 40px;

    .add-new-row {
        .p-inputtext {
            height: 48px !important;
        }
        .core-percentage-input.p-inputnumber-buttons-horizontal.core-percentage-input--inline
            > .p-button-icon-only {
            height: 48px !important;
        }
    }

    .header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;

        margin-bottom: 40px;

        h3 {
            font-style: normal;
            font-weight: bold;
            font-size: 24px;
            line-height: 28px;

            color: var(--color-primary-500);
        }

        .close {
            position: relative;
            width: 20px;
            height: 20px;

            cursor: pointer;

            &::before {
                content: '';
                position: absolute;
                right: 0;
                top: 0;
                height: 20px;
                width: 2px;
                background: var(--color-primary-500);

                transform: rotate(45deg);
            }
            &::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                height: 20px;
                width: 2px;
                background: var(--color-primary-500);

                transform: rotate(-45deg);
            }
        }
    }
}
</style>
